import { accessibleBrandColor, WCAG_AAA_LARGE_TEXT_CONTRAST } from 'utils/accessibleBrandColor';
import { readableTextColor } from 'utils/readableTextColor';
import { DEFAULT_HEADLINE_COLOR } from '@/constants/brandColors';

interface HeadingColorInput {
  /** The text block's own background color, which is the surface the heading sits on. */
  textBlockColor: string;
  /** Whether that color was chosen by the customer rather than defaulted. */
  hasCustomTextBlockColor: boolean;
  /** The brand's headline color, as published. */
  headlineColor?: string;
}

/**
 * The color the hero's <h1> renders in.
 *
 * globals.css paints every h1–h6 with the brand headline color, and an element rule
 * beats the color the text block passes down by inheritance — so a pale brand headline
 * disappears into the block unless it is checked here.
 *
 * A customer-chosen block color keeps the heading matching the block's body text. On the
 * default block the brand headline color is kept whenever it reads against the block, and
 * shaded when it does not. The heading renders at 2em, which WCAG counts as large text, so
 * its AAA threshold is 4.5:1 rather than the 7:1 that applies to body-size text.
 *
 * The block's opacity lets the hero background show through, so the block color is a close
 * approximation of the true surface rather than an exact match — the same approximation the
 * block's own body text color already makes.
 */
export function heroHeadingColor({
  textBlockColor,
  hasCustomTextBlockColor,
  headlineColor,
}: HeadingColorInput): string {
  if (hasCustomTextBlockColor) {
    return readableTextColor(textBlockColor);
  }
  return accessibleBrandColor([headlineColor || DEFAULT_HEADLINE_COLOR], textBlockColor, WCAG_AAA_LARGE_TEXT_CONTRAST);
}
